import React from 'react';
import { Input, InputProps } from '@apia/theme';
import { lang } from '../../../../store/validation';
import { getFieldErrorStyles } from '../../../apia/execution/style';
import { useFormContext } from '../FormContext';
import useField, { TUseField } from '../hooks/useField';
import { TDefaultFieldType } from './types';
import useValidationClass from './useValidationClass';
import Wrapper, { IWrapper } from './Wrapper';

type TFileInput = Omit<InputProps, 'onChange' | 'type' | 'value' | 'multiple'> &
  TDefaultFieldType<File | null> &
  Pick<TUseField, 'removeOnUnmount'> &
  Pick<IWrapper, 'avoidSemicolon'>;

const FileInput = React.forwardRef<HTMLInputElement, TFileInput>(
  (
    {
      avoidSemicolon,
      className,
      label,
      /**
       * useField
       */
      avoidRegisterIfExists,
      formName: propsFormName,
      initialValue,
      name,
      removeOnUnmount,
      submitValueParser,
      validationClass,
      validationFunction,
      validationRules,
      ...props
    },
    ref,
  ) => {
    const { name: formName } = useFormContext();
    const { actualValidationFunction, actualValidationRules } =
      useValidationClass({
        formName,
        name,
        customValidation(fileValue) {
          return fileValue !== null && fileValue !== undefined
            ? true
            : lang('required');
        },
        validationClass,
        validationFunction,
        validationRules,
      });
    const {
      error,
      onChange,
      value,
      updateValidations,
      isTouched,
      ...validationProps
    } = useField<File | null>({
      formName,
      name,
      removeOnUnmount,
      submitValueParser,
      validationFunction: actualValidationFunction,
      validationRules: actualValidationRules,
    });

    return (
      <Wrapper
        name={name}
        label={label}
        className={`input ${className ?? ''}`}
        avoidSemicolon={avoidSemicolon}
      >
        <Input
          {...props}
          {...validationProps}
          type="file"
          aria-label={props['aria-label'] ?? label}
          ref={ref}
          sx={getFieldErrorStyles(!error)}
          onChange={React.useCallback(
            (ev: React.ChangeEvent<HTMLInputElement>) => {
              onChange(ev.target?.files?.[0] ?? null);
            },
            [onChange],
          )}
        />
      </Wrapper>
    );
  },
);

export default React.memo(FileInput);
